import { useMemo } from 'react'
import { ResultAsync } from 'neverthrow'
import { GlobalStore } from '@highlight/ipc'
import { getChatApiRoute } from '../api/v1Client'
import { connectToTurnStream } from '../engine/errors'
import type { ChatApi, ChatRequestBody } from '../engine/services'
import { useAuth } from '../providers'
import { postChatTurn } from './chat-turn-post'
import { useIsAgenticChatEnabled } from './use-agentic-chat'
import { DISABLE_CAPTCHA, useIsSendChallengeEnabled } from './use-send-challenge'

// BYOK (Bring Your Own Key) configuration
const API_PROVIDERS = {
  OPENAI: {
    settingKey: 'openaiApiKey',
    headerKey: 'x-hl-openai-api-key',
  },
  ANTHROPIC: {
    settingKey: 'anthropicApiKey',
    headerKey: 'x-hl-anthropic-api-key',
  },
  GOOGLE: {
    settingKey: 'googleApiKey',
    headerKey: 'x-hl-google-api-key',
  },
} as const

function byokHeaders(): Record<string, string> {
  return Object.values(API_PROVIDERS).reduce(
    (headers, { settingKey, headerKey }) => {
      const apiKey = GlobalStore.settings().get(settingKey) as string | null
      return apiKey ? { ...headers, [headerKey]: apiKey } : headers
    },
    {} as Record<string, string>,
  )
}

/** The fields that pick the chat mode. The prepare call sends the same ones, so it prepares the turn the send runs. */
export function chatModeFields(
  isAgenticChatEnabled: boolean,
): Pick<ChatRequestBody, 'useAgenticChat' | 'useConsolidatedTools'> {
  return isAgenticChatEnabled ? { useAgenticChat: true } : { useConsolidatedTools: true }
}

/**
 * The engine's `ChatApi`, bound to the real auth/captcha/BYOK plumbing: the
 * `/subscribe` GET for resume and the `/chat` POST for submit.
 */
export function useChatApi(): ChatApi {
  const { getAuthToken } = useAuth()
  const isSendChallengeEnabled = useIsSendChallengeEnabled()
  const isAgenticChatEnabled = useIsAgenticChatEnabled()

  return useMemo<ChatApi>(
    () => ({
      subscribeToTurnStream: (chatId, signal, lastEventId) =>
        connectToTurnStream({ phase: 'resume', chatId }, async () => {
          const accessToken = await getAuthToken()
          return fetch(getChatApiRoute(`api/v1/chat/${chatId}/subscribe`), {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              ...(lastEventId !== undefined ? { 'Last-Event-ID': String(lastEventId) } : {}),
            },
            signal,
          })
        }),

      startTurnStream: (chatId, body, signal, turnId) =>
        new ResultAsync(
          postChatTurn({
            chatId,
            turnId,
            body: { ...body, ...chatModeFields(isAgenticChatEnabled) },
            signal,
            getAuthToken,
            getByokHeaders: byokHeaders,
            isSendChallengeEnabled,
            isChallengeOptedOut: DISABLE_CAPTCHA,
          }),
        ),
    }),
    [getAuthToken, isSendChallengeEnabled, isAgenticChatEnabled],
  )
}
